<ng-container *ngIf="!isUserLoggedIn">
	<app-ehe-header></app-ehe-header>
</ng-container>
<ng-container *ngIf="isUserLoggedIn">
	<app-ehe-header [auth]="true"></app-ehe-header>
</ng-container>
<section class="static-content-wrapper general-wrapper">
	<div class="container">
		<div class="row">
			<div class="col-md-12">
				<div class="title-container">
					<h3>PRIVACY POLICY</h3>
					<p>Last updated: June 27, 2024</p>
				</div>

				<div class="group">
					<p>
						This Privacy Policy describes how EHE Health ("EHE," "we" or "us") collects, uses, and discloses information
						about you that you provide through the EHE website, myEHE portal, mobile applications, and any other
						websites or mobile applications that we own and operate, and that link to this Privacy Policy (collectively,
						the "Services"). Certain parts of the Services are available only to individuals who create an account on
						the Services ("Users").
					</p>
				</div>

				<div class="group">
					<p>
						If information you provide through the Services is protected health information, we will use and disclose
						that information only as stated in the applicable
						<a target="_blank" [routerLink]="['/notice-of-privacy-practices']">Notice of Privacy Practices</a>. If you
						have any questions about which policy applies to information you provide, please contact us at the contact
						information provided in the "Contact Us" section below.
					</p>
				</div>

				<div class="group">
					<p>
						If you do not want us to collect, use or disclose information about you as described in this Privacy Policy,
						then you should not use the Services.
						<strong
							>By using the Services (including visiting the EHE website, using myEHE portal, and using any of our
							mobile apps), you are accepting the practices set out in this Privacy Policy.</strong
						>
					</p>
				</div>

				<div class="group">
					<p class="general-title">INFORMATION WE COLLECT AND HOW WE USE THAT INFORMATION</p>
					<p>
						We may collect different types of information about you based on your use of our Services and our business
						relationship with you.
					</p>
					<p>
						Specifically, we collect the following personal information, and may have collected such information in the
						past 12 months:
					</p>

					<div class="col-md-12">
						<table class="summary-table" border="1px solid black">
							<thead>
								<tr>
									<th>Category</th>
									<th>Specific Information Collected</th>
									<th>Source and Purpose</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Identifiers</td>
									<td>
										Name, email address, physical address, phone number, Social Security number (this is considered
										sensitive information under some laws), employee identification number
									</td>
									<td>
										Collected from you during registration and from employers, for the purposes of providing the
										Services and for marketing via the contact information that you provide
									</td>
								</tr>
								<tr>
									<td>Protected information per Cal. Civ. Code § 1798.80(e)</td>
									<td>
										Name, address, phone number, age, insurance policy number, Social Security number, employee
										identification number
									</td>
									<td>
										Collected from you during registration and from employers, for the purposes of providing the
										Services
									</td>
								</tr>
								<tr>
									<td>Characteristics of protected classifications</td>
									<td>Age, sex, medical condition, medical symptoms, information from your wearable devices</td>
									<td>
										Collected from you, your healthcare providers, and from partners on our platform with which Users
										interact, for the purpose of providing the Services
									</td>
								</tr>
								<tr>
									<td>Commercial Information</td>
									<td>Records of services and products used</td>
									<td>
										Collected automatically when users make purchases for purposes of providing our Service, promoting
										our services, and analyzing and improving our services
									</td>
								</tr>
								<tr>
									<td>Internet or other electronic network activity information</td>
									<td>
										Information regarding your interaction with the Services, including your IP address and your device
										identifier
									</td>
									<td>
										Collected automatically when you use our Services (for example, when you visit our website or sign
										in to myEHE or to our mobile apps), for purposes of analytics, improving the Services, and for
										marketing
									</td>
								</tr>
								<tr>
									<td>
										Inferences from information we collect when you visit our sites, information collected from third
										parties or information about consumer preferences
									</td>
									<td>Information regarding your preferences, behavior, and abilities</td>
									<td>Collected from you and from wearable devices for the purposes of providing the Services</td>
								</tr>
							</tbody>
						</table>
					</div>

					<br />

					<p>
						In addition to the purposes listed above, we may use the information we collect for the following purposes:
					</p>
					<ul>
						<li>Provide you with a better customer experience when you use our Services.</li>
						<li>Communicate with you regarding service updates, offers, and promotions.</li>
						<li>Deliver customized content and advertising that may be of interest to you.</li>
						<li>Maintain the security of our products, services, and systems.</li>
						<li>Protect our rights, property, and safety or the rights, property, and safety of others.</li>
						<li>Improve our level of service or develop new products or services; and</li>
						<li>Comply with our legal obligations.</li>
					</ul>
					<p>
						We retain personal information for as long as required under applicable laws and contractual obligations.
					</p>
				</div>

				<div class="group">
					<p class="general-title">ONLINE ACTIVITY TRACKING</p>
					<p>
						When you visit our website, we gather certain information automatically and store it. This information may
						include internet protocol (IP) addresses, browser type, internet service provider), referring/exit pages,
						operating system, date/time stamp, and clickstream data. EHE, our partners, and analytics or service
						providers use technologies such as cookies, beacons, tags, and scripts, to analyze trends, administer and
						track utilization of the website.
					</p>
					<p>
						We may collect information about your computer, including where available your IP address, operating system,
						and browser type. We use "cookies" to remember your settings and for authentication. A cookie is a piece of
						data stored on a site visitor's hard drive to help us improve your access to our site and identify repeat
						visitors to our site. For instance, when we use a cookie to identify you, you would not have to log in a
						password more than once, thereby saving time while on our site. Cookies can also enable us to deliver
						personalized experience on our site. Cookies will not be used to store personally identifiable information.
						We use beacons and tags to analyze and administer the use of our website and to optimize marketing.
					</p>
					<p>
						You can control the use of cookies with your browser settings. If you reject cookies, you may not be able to
						log in to our website or use all its features.
					</p>
					<p>
						Throughout our websites you may encounter social media features that provide the option to "follow us."
						These social media features may collect your IP address and enable cookies. The social media features found
						on our website are hosted by a third party or hosted directly by our website. Your interactions with these
						features are governed by the privacy policy of the company providing it.
					</p>
				</div>

				<div class="group">
					<p class="general-title">HOW WE SHARE INFORMATION</p>
					<p>We do not sell your personal information to anyone for direct marketing purposes without your consent.</p>
					<p>We may share your information with the following third parties:</p>
					<ul>
						<li>Our subsidiaries and affiliates.</li>
						<li>
							Contractors, service providers, and other third parties we use to support our business and provide you
							with our Services (such as advisors, lawyers, web analytics providers, and data management vendors) and
							who are bound by contractual obligations to keep personal information confidential and secure, and to use
							it only for the purposes for which we disclose it to them.
						</li>
						<li>
							Partners on our mobile app platform who offer health or wellness services that may be of interest to you,
							for the purposes of facilitating such services to you.
						</li>
						<li>
							Buyers or other successors in the event of a merger, divestiture, restructuring, reorganization,
							dissolution or other sale or transfer of some or all our assets, whether as a going concern or as part of
							bankruptcy, liquidation or similar proceeding, in which personal information held by us is among the
							assets transferred.
						</li>
						<li>
							Government entities or other parties to comply with court order, law, or legal process, including
							responding to a government, court, or regulatory request.
						</li>
						<li>
							Government entities, third-party cybersecurity experts, or other parties as necessary and appropriate to
							ensure the security and integrity of our networks, or otherwise protect the rights, property, or safety of
							EHE, our customers, or others.
						</li>
						<li>
							Third-party entities to enforce or apply our terms of use and other agreements, or otherwise protect or
							assert our legal rights, including for billing and collection purposes.
						</li>
						<li>
							Your health plan or employer, if our services are part of a program or benefit that they offer to you, or
							to your health care providers as part of our products or services.
						</li>
					</ul>
					<p>
						In the myEHE portal, you control who can access your health information. By default, you are the only user
						who can view your information. If you choose to, you can share your information with others. Only your
						healthcare providers can upload data onto your Online Medical Record on the myEHE portal. If there is an
						omission, error, or issue with the data in your Online Medical Record, immediately contact the applicable
						healthcare provider.
					</p>
					<p>
						If you use any bulletin board, chat room, comment posting feature, or other public communication service,
						forum, or feature offered through the Services, or post any information available for viewing by other
						Users, any of the information that you share will be visible to other Users. The information that you make
						available can be read, used, and collected by other Users to send you unsolicited messages outside of EHE
						products and services. EHE is not responsible for the manner in which the information that you decide to
						share will be used by other Users.
					</p>
				</div>

				<div class="group">
					<p class="general-title">NO TRANSFER OF PERSONAL DATA TO OTHER COUNTRIES</p>
					<p>
						EHE Health is committed to protecting the privacy and security of personal information entrusted to us by
						our customers. As part of our dedication to maintaining the highest standards of data privacy, we adhere to
						strict policies regarding the transfer of personal information to other countries.
					</p>

					<p>
						EHE Health does not transfer any personal information outside of the United States of America. We maintain
						all data processing activities within the boundaries of the United States to ensure compliance with relevant
						data protection regulations and to uphold the privacy expectations of our customers and stakeholders.
					</p>

					<p>
						EHE Health regularly reviews and updates its data privacy policies and practices to ensure ongoing
						compliance with regulatory requirements and best practices in data protection. Any changes to our data
						transfer practices will be reflected in updates to this policy.
					</p>
				</div>

				<div class="group">
					<p class="general-title">ANONYMOUS & AGGREGATE INFORMATION</p>
					<p>
						We collect some information on an anonymous basis. We also may anonymize or de-identify the information we
						collect about you so that it can no longer be linked to you, and we may combine or aggregate this data, or
						engage service providers to do so on our behalf. We may use and share this data with third parties to
						perform data analytics and research, including to improve our products or services or develop new products
						or services.
					</p>
				</div>

				<div class="group">
					<p class="general-title">OUR ONLINE PRIVACY POLICY FOR CHILDREN</p>
					<p>
						Our products and services are not designed to attract children under the age of 13. We do not knowingly
						collect personally identifying information from anyone under the age of 13. Any information about a child
						under the age of 13 must be provided by that child's parent or legal guardian. If you are the parent or
						legal guardian of a child under the age of 13 and become aware that the child has provided personal
						information through the Service without your consent, please notify us promptly. If we become aware that a
						child under age 13 has provided us with personal information without parental/guardian consent, we will take
						steps to remove it.
					</p>
				</div>

				<div class="group">
					<p class="general-title">USE OF GOOGLE APIS</p>
					<p>
						Where Pulse by EHE Health uses and / or transfers information received from Google APIs to any other app, it
						will adhere to the
						<a
							target="_blank"
							href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
							>Google API Services User Data Policy</a
						>, including the Limited Use requirements.
					</p>
				</div>

				<div class="group">
					<p class="general-title">YOUR CHOICES</p>
					<p>
						Residents of certain states, including California, Virginia, Colorado, and Utah may have or will have the
						right to: (1) know what personal information we collect, use, disclose, and sell; (2) request that we delete
						your personal information; (3) access and obtain a copy of your personal information that we collect; (4)
						request correction of inaccurate personal information; (5) opt out of targeted advertising; or (6) limit the
						use and disclosure of certain sensitive information. We will honor your request to exercise any of these
						rights to the extent required by applicable law and will make a good faith effort to honor your request in
						all other cases. If you disagree with our response to your request, you may appeal the request by submitting
						supporting documentation to privacy&#64;ehe.health.
					</p>
					<p>
						With respect to deletion requests, we cannot delete your personal information except by also deleting your
						user account. We may not accommodate a request to change information if we believe the change would violate
						any law or legal requirement or cause the information to be incorrect.
					</p>
					<p>
						We may not discriminate against you for exercising any of your rights. This means that we may not deny you
						services or charge you different prices or rates for services or provide you with a different level or
						quality of services (or suggest that we will do so), in response to these requests. However, we may charge
						different prices or rates, or provide a different level or quality of services, where that difference is
						reasonably related to the value provided to us by your personal information.
					</p>
					<p>You may request to exercise the foregoing rights by:</p>
					<ul>
						<li>Emailing us at privacy&#64;ehe.health</li>
						<li>Calling us toll-free at {{ config.contactInfo.phone_prefix_1_dots }}</li>
					</ul>
					<p>
						Please note that we may take steps to verify your identity before granting you access to information or
						acting on your request to exercise your rights, as required or permitted by law. We may limit our response
						to your exercise of the above rights as permitted by law.
					</p>
				</div>

				<div class="group">
					<p class="general-title">EMAIL AND TEXT COMMUNICATIONS</p>
					<p>
						We may communicate with you, using the contact information provided by you or your employer, electronically
						or via telephone, including by SMS (text) about our programs, products, services, offers or general health
						information. We may communicate with you via unencrypted methods, such as by text or unencrypted email. You
						acknowledge and accept that communications by text or email involve some risk of unauthorized disclosure or
						interception of the contents of these communications. You may customize your preferences on our app or myEHE
						portal for some communications, including delivery options, and update your contact information, including
						telephone number and email address. It is your responsibility to provide us with true, accurate, and
						complete contact information, and to make any changes to that information promptly. You understand that
						failing to update your contact information may delay or prevent us providing you with communications that
						rely on that contact information.
					</p>
				</div>

				<div class="group">
					<p class="general-title">THIRD PARTIES AND LINKS</p>
					<p>
						The Services allow you to use the services of our partners, such as healthcare providers participating on
						our mobile app platform. If you provide your information to any third party (such as healthcare provider
						partners) via the Services, you understand that EHE is not responsible for such third parties' use or misuse
						of your information. You should review the privacy and information sharing practices of such third parties.
						Additionally, EHE may offer links to or from the Services to other sites, so that you can conveniently visit
						our vendors and advertisers or locate other content likely to be of interest. We are not responsible for the
						content, practices or policies of sites operated by third parties and urge you to inquire about them before
						providing any personally identifiable information.
					</p>
				</div>

				<div class="group">
					<p class="general-title">INFORMATION SECURITY</p>
					<p>
						We employ industry standard administrative, physical, and technical measures designed to safeguard and
						protect information under our controlled authorized access, use, and disclosure. Access to your personal
						information is restricted to only authorized EHE employees and certain authorized partners involved in the
						delivery of our services. While we use encryption to protect sensitive electronic information, we also
						protect your information offline. Only employees who need the information to perform a specific, necessary
						function are granted access to personally identifiable information. The computers/servers in which we store
						personally identifiable information are kept in a secure environment.
					</p>
				</div>

				<div class="group">
					<p class="general-title">DATA SUBJECT ACCESS REQUEST</p>
					<p>
						To submit a Data Subject Access Request, please complete this
						<a href="https://my.datasubject.com/AzqJMPTLxPd6b2C71/23773" target="_blank">DSAR Form</a>.
					</p>
				</div>

				<div class="group">
					<p class="general-title">CHANGES TO OUR PRIVACY POLICY</p>
					<p>
						We may make changes to our Privacy Policy from time to time. When we do so, we will post the revised Privacy
						Policy on our Services and change the "Effective Date". Please check the Effective Date at the top of this
						page to determine if the policy has been modified since you last reviewed it. Your continued use of the
						Services after that date means you agree to this Privacy Policy and any changes to it.
					</p>
				</div>

				<div class="group">
					<p class="general-title">CONTACT US</p>
					<p>You may contact us at the following addresses:</p>
					<ul>
						<li>Email: &nbsp;<a href="mailto:privacy@ehe.health">privacy&#64;ehe.health</a></li>
						<li>
							Address:
							<ul style="list-style: none">
								<li>
									EHE Health
									<br />Privacy & Security Officer <br />600 Fifth Avenue, 5th Floor <br />New York, NY 10020
								</li>
							</ul>
						</li>
					</ul>
				</div>
			</div>
		</div>
	</div>
</section>
<app-ehe-footer [socialNetwork]="true"></app-ehe-footer>
